import {
  AcknowledgementStatus,
  ApprovalDecision,
  ApprovalPolicy,
  AccessRuleEffect,
  AccessRuleScope,
  AuditAction,
  DocumentPermissionFlag,
  DocumentVersionStatus,
  SystemRole,
  ValidityState,
} from '@qualicloud/contracts';

/**
 * Dicionário pt-BR.
 *
 * Este é o dicionário de referência: `Dictionary` é inferido daqui, então
 * qualquer outro idioma é tipado como `Partial<Dictionary>` e cai de volta no
 * português quando faltar chave. Português é o idioma da norma que o cliente lê
 * e do auditor da ANVISA (architecture.md §12).
 *
 * Os rótulos de ENUM ficam separados do texto de interface porque são
 * traduções de valores de domínio: eles aparecem em tela, em CSV exportado e no
 * PDF/A, e precisam ser exatamente os mesmos nos três.
 */
export const ptBR = {
  common: {
    appName: 'QualiCloud eQMS',
    save: 'Salvar',
    cancel: 'Cancelar',
    close: 'Fechar',
    confirm: 'Confirmar',
    back: 'Voltar',
    next: 'Avançar',
    search: 'Buscar',
    filter: 'Filtrar',
    clear: 'Limpar',
    clearFilters: 'Limpar filtros',
    export: 'Exportar',
    print: 'Imprimir',
    download: 'Baixar',
    edit: 'Editar',
    remove: 'Remover',
    add: 'Adicionar',
    create: 'Criar',
    view: 'Visualizar',
    loading: 'Carregando…',
    retry: 'Tentar novamente',
    required: 'obrigatório',
    optional: 'opcional',
    yes: 'Sim',
    no: 'Não',
    all: 'Todos',
    none: 'Nenhum',
    of: 'de',
    actions: 'Ações',
    details: 'Detalhes',
    unknown: 'Não informado',
  },

  nav: {
    dashboard: 'Painel',
    masterList: 'Lista Mestra',
    approvals: 'Minhas aprovações',
    documentTypes: 'Tipos de documento',
    auditTrail: 'Trilha de auditoria',
    organization: 'Organização',
    users: 'Usuários',
    departments: 'Departamentos',
    notifications: 'Notificações',
    quality: 'Qualidade',
    administration: 'Administração',
    skipToContent: 'Pular para o conteúdo principal',
  },

  auth: {
    signIn: 'Entrar',
    signOut: 'Sair',
    username: 'Usuário',
    password: 'Senha',
    organization: 'Organização',
    mfaCode: 'Código de verificação',
    mfaHint: 'Código de 6 dígitos do seu aplicativo autenticador.',
    changePassword: 'Trocar senha',
    currentPassword: 'Senha atual',
    newPassword: 'Nova senha',
    confirmPassword: 'Confirmar nova senha',
    passwordStrength: 'Força da senha',
    mustChangePasswordTitle: 'Defina uma nova senha',
    mustChangePasswordBody:
      'Sua senha expirou ou precisa ser trocada no primeiro acesso. Defina uma nova senha para continuar.',
    lockedUntil: 'Bloqueada até',
  },

  signature: {
    title: 'Assinatura eletrônica',
    subtitle: 'Confirme sua identidade para concluir este ato.',
    whatIsBeingSigned: 'O que será assinado',
    meaning: 'Significado desta assinatura',
    signer: 'Signatário',
    reason: 'Justificativa',
    reasonRequired: 'Justificativa (obrigatória para este ato)',
    legallyBinding:
      'Esta assinatura eletrônica é o equivalente legal da sua assinatura manuscrita, conforme 21 CFR Part 11. O ato será registrado de forma permanente e não poderá ser desfeito.',
    identityConfirmation: 'Confirmação de identidade',
    identityHelp:
      'Informe seu usuário e sua senha. Os dois componentes são exigidos pelo §11.200(a)(1)(i), mesmo com a sessão já aberta.',
    sign: 'Assinar',
    signing: 'Registrando a assinatura…',
    requestingProof: 'Validando sua identidade…',
    countdownLabel: 'Tempo restante para concluir',
    expired: 'O prazo expirou',
    success: 'Assinatura registrada',
    manifestTitle: 'ASSINATURAS ELETRÔNICAS',
    manifestFooter:
      'Assinaturas eletrônicas aplicadas conforme 21 CFR Part 11 e legalmente vinculantes.',
    manifestEmpty: 'Este registro ainda não possui assinaturas eletrônicas.',
    hash: 'Hash',
    authMethodPassword: 'Usuário e senha',
    authMethodPasswordMfa: 'Usuário, senha e segundo fator',
  },

  masterList: {
    title: 'Lista Mestra de Documentos',
    typeTree: 'Tipos de documento',
    columnCode: 'Código',
    columnName: 'Nome do documento',
    columnType: 'Tipo',
    columnVersion: 'Versão',
    columnRevisionDate: 'Data da versão',
    columnValidUntil: 'Válido até',
    columnDaysLeft: 'Dias restantes',
    columnStatus: 'Situação',
    filterExpiring: 'Vencendo em (dias)',
    filterPendingMyApproval: 'Aguardando minha aprovação',
    filterPendingMyAck: 'Aguardando minha ciência',
    filterIncludeDescendants: 'Incluir subtipos',
    noValidity: 'Sem validade',
    expiredBy: (days: number) => `Vencido há ${days} ${days === 1 ? 'dia' : 'dias'}`,
    expiresIn: (days: number) => `Vence em ${days} ${days === 1 ? 'dia' : 'dias'}`,
    expiresToday: 'Vence hoje',
  },

  validity: {
    expired: 'Vencido',
    expiringSoon: 'Vencendo',
    valid: 'Válido',
    notApplicable: 'Sem validade',
  },

  errors: {
    boundaryTitle: 'Algo deu errado nesta tela',
    boundaryBody:
      'A operação não foi concluída e nada foi alterado. Você pode tentar recarregar. Se o problema continuar, informe o código abaixo ao suporte.',
    notFoundTitle: 'Página não encontrada',
    notFoundBody: 'O endereço acessado não existe ou você não tem permissão para vê-lo.',
    forbiddenTitle: 'Acesso não autorizado',
    forbiddenBody:
      'Você não tem permissão para acessar esta área. Se acredita que deveria ter, procure o administrador da sua organização.',
    correlationId: 'Código de correlação',
  },
} as const;

export type Dictionary = typeof ptBR;

// ─────────────────────────────────────────────────────────────────────────────
// Rótulos de enum
// ─────────────────────────────────────────────────────────────────────────────

/** Situação da versão. Substitui os `CHAR(1)` do legado ('A','P','R','V','D'). */
export const DOCUMENT_STATUS_LABEL: Record<DocumentVersionStatus, string> = {
  [DocumentVersionStatus.DRAFT]: 'Rascunho',
  [DocumentVersionStatus.IN_REVIEW]: 'Pendente de aprovação',
  [DocumentVersionStatus.APPROVED]: 'Aprovado',
  [DocumentVersionStatus.REJECTED]: 'Reprovado',
  [DocumentVersionStatus.EXPIRED]: 'Vencido',
  [DocumentVersionStatus.SUPERSEDED]: 'Versão superada',
  [DocumentVersionStatus.OBSOLETE]: 'Obsoleto',
};

export const VALIDITY_STATE_LABEL: Record<ValidityState, string> = {
  [ValidityState.NOT_APPLICABLE]: 'Sem validade',
  [ValidityState.VALID]: 'Válido',
  [ValidityState.EXPIRING_SOON]: 'Vencendo',
  [ValidityState.EXPIRED]: 'Vencido',
};

export const APPROVAL_POLICY_LABEL: Record<ApprovalPolicy, string> = {
  [ApprovalPolicy.ANY_ONE]: 'Qualquer aprovador',
  [ApprovalPolicy.ALL_REQUIRED]: 'Todos os aprovadores',
  [ApprovalPolicy.QUORUM]: 'Quórum de N aprovadores',
};

export const APPROVAL_POLICY_DESCRIPTION: Record<ApprovalPolicy, string> = {
  [ApprovalPolicy.ANY_ONE]:
    'Uma única aprovação promove a versão.',
  [ApprovalPolicy.ALL_REQUIRED]:
    'Todos os aprovadores designados na distribuição precisam aprovar.',
  [ApprovalPolicy.QUORUM]:
    'Um número mínimo de aprovações, definido no campo Quórum, dentro do conjunto de aprovadores.',
};

export const APPROVAL_DECISION_LABEL: Record<ApprovalDecision, string> = {
  [ApprovalDecision.PENDING]: 'Pendente',
  [ApprovalDecision.APPROVED]: 'Aprovou',
  [ApprovalDecision.REJECTED]: 'Reprovou',
};

export const ACKNOWLEDGEMENT_STATUS_LABEL: Record<AcknowledgementStatus, string> = {
  [AcknowledgementStatus.PENDING]: 'Ciência pendente',
  [AcknowledgementStatus.ACKNOWLEDGED]: 'Ciência confirmada',
  [AcknowledgementStatus.SUPERSEDED]: 'Encerrada sem ciência',
};

/** Rótulos das 6 flags de ACL. Nomes do legado entre parênteses onde ajuda. */
export const PERMISSION_FLAG_LABEL: Record<DocumentPermissionFlag, string> = {
  [DocumentPermissionFlag.READ]: 'Visualizar',
  [DocumentPermissionFlag.WRITE]: 'Editar',
  [DocumentPermissionFlag.DELETE]: 'Excluir',
  [DocumentPermissionFlag.DOWNLOAD]: 'Baixar e imprimir',
  [DocumentPermissionFlag.APPROVE]: 'Aprovar',
  [DocumentPermissionFlag.SECURITY]: 'Administrar distribuição',
};

export const PERMISSION_FLAG_DESCRIPTION: Record<DocumentPermissionFlag, string> = {
  [DocumentPermissionFlag.READ]: 'Ver o documento e seus metadados na Lista Mestra.',
  [DocumentPermissionFlag.WRITE]: 'Emitir nova versão do documento.',
  [DocumentPermissionFlag.DELETE]: 'Excluir o documento.',
  [DocumentPermissionFlag.DOWNLOAD]: 'Baixar e imprimir o arquivo binário.',
  [DocumentPermissionFlag.APPROVE]: 'Aprovar ou reprovar versões, com assinatura eletrônica.',
  [DocumentPermissionFlag.SECURITY]:
    'Obsoletar o documento e reconfigurar quem tem acesso.',
};

export const ACCESS_SCOPE_LABEL: Record<AccessRuleScope, string> = {
  [AccessRuleScope.USER]: 'Colaborador',
  [AccessRuleScope.DEPARTMENT]: 'Departamento',
  [AccessRuleScope.ORGANIZATION]: 'Toda a organização',
};

export const ACCESS_EFFECT_LABEL: Record<AccessRuleEffect, string> = {
  [AccessRuleEffect.ALLOW]: 'Permitir',
  [AccessRuleEffect.DENY]: 'Negar',
};

export const SYSTEM_ROLE_LABEL: Record<SystemRole, string> = {
  [SystemRole.PLATFORM_ADMIN]: 'Administrador da plataforma (fornecedor)',
  [SystemRole.ORG_ADMIN]: 'Administrador da organização',
  [SystemRole.QUALITY_MANAGER]: 'Gestor da qualidade',
  [SystemRole.DEPARTMENT_MANAGER]: 'Gerente de departamento',
  [SystemRole.MEMBER]: 'Colaborador',
  [SystemRole.AUDITOR]: 'Auditor',
};

export const AUDIT_ACTION_LABEL: Record<AuditAction, string> = {
  [AuditAction.CREATE]: 'Criação',
  [AuditAction.UPDATE]: 'Alteração',
  [AuditAction.DELETE]: 'Exclusão',
  [AuditAction.READ]: 'Leitura',
  [AuditAction.APPROVE]: 'Aprovação',
  [AuditAction.REJECT]: 'Reprovação',
  [AuditAction.SIGN]: 'Assinatura eletrônica',
  [AuditAction.LOGIN]: 'Entrada no sistema',
  [AuditAction.LOGIN_FAILED]: 'Tentativa de entrada malsucedida',
  [AuditAction.LOGOUT]: 'Saída do sistema',
  [AuditAction.SESSION_EXPIRED]: 'Sessão expirada',
  [AuditAction.LOCKOUT]: 'Bloqueio de conta',
  [AuditAction.UNLOCK]: 'Desbloqueio de conta',
  [AuditAction.PASSWORD_CHANGE]: 'Troca de senha',
  [AuditAction.PASSWORD_RESET]: 'Redefinição de senha',
  [AuditAction.PERMISSION_GRANT]: 'Concessão de permissão',
  [AuditAction.PERMISSION_REVOKE]: 'Revogação de permissão',
  [AuditAction.ROLE_CHANGE]: 'Alteração de papel',
  [AuditAction.DOWNLOAD]: 'Download',
  [AuditAction.PRINT]: 'Impressão',
  [AuditAction.EXPORT]: 'Exportação',
  [AuditAction.CONFIG_CHANGE]: 'Alteração de configuração',
  [AuditAction.VENDOR_ACCESS]: 'Acesso do fornecedor',
  [AuditAction.DATA_MIGRATION]: 'Migração de dados',
  [AuditAction.OBSOLETE]: 'Obsolescência',
  [AuditAction.ACKNOWLEDGE]: 'Ciência',
};

export const NOTICE_FREQUENCY_LABEL: Record<'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY', string> = {
  NONE: 'Sem aviso',
  DAILY: 'Diário',
  WEEKLY: 'Semanal',
  MONTHLY: 'Mensal',
};
